// Page changes without a page load, so the sound bed (and the audio context it lives in)
// carries across the whole site: same-origin links fetch the page and swap <main>, the
// header stays. Forms, downloads, new-tab links and the sign-in dance still go the normal way.
import { track } from "./telemetry.ts";
import { swapMain } from "./page.ts";

const skip = (path: string) =>
  path.startsWith("/auth/") ||
  path.startsWith("/static/") ||
  path.startsWith("/admin/") ||
  path.endsWith(".xml") ||
  path.endsWith(".txt") ||
  path.endsWith(".rss") ||
  path.endsWith(".json") ||
  path.endsWith("/json") ||
  path.startsWith("/embed/") ||
  path.startsWith("/oembed") ||
  path.startsWith("/songs/") ||
  path.includes("/events") ||
  path.startsWith("/ring/");

/** which nav link is "on" for a path */
export const section = (path: string) => {
  if (path.startsWith("/bouts")) return "/bouts";
  if (path === "/" || path.startsWith("/bout")) return "/";
  if (path.startsWith("/banzuke") || path.startsWith("/contender/")) return "/banzuke";
  if (path.startsWith("/rules")) return "/rules";
  if (path.startsWith("/about") || path.startsWith("/whitepaper")) return "/about";
  if (path.startsWith("/enter")) return "/enter";
  if (path === "/me" || path === "/login") return "/me";
  return "";
};
const markNav = () => {
  const on = section(location.pathname);
  document.querySelectorAll("header a[href]").forEach((a) => {
    const p = new URL((a as HTMLAnchorElement).href, location.origin).pathname;
    const mine = p === on || (on === "/me" && (p === "/me" || p === "/login"));
    a.classList.toggle("on", (mine && p !== "/enter") || (on === "/enter" && p === "/enter"));
  });
};

let going: ((href: string, push: boolean) => Promise<void>) | null = null;
/** the page again, in place (a quiet form landed): nothing pushed, the bar untouched */
export const refresh = () => (going ? going(location.href, false) : Promise.resolve());

export function initNav({ leave, arrive }: { leave: () => void; arrive: () => void }) {
  let busy = false;
  const go = async (href: string, push: boolean) => {
    if (busy) return;
    busy = true;
    try {
      const { doc, url } = await swapMain(href, { onBlank: leave });
      document.title = doc.title;
      // the signed-in handle can change (sign in / out land here after their redirects)
      const freshMe = doc.querySelector("header a.me"),
        curMe = document.querySelector("header a.me");
      if (freshMe && curMe) curMe.replaceWith(freshMe);
      if (push) history.pushState({ basho: 1 }, "", url);
      markNav();
      document.documentElement.removeAttribute("data-drawer-open");
      const toggle = document.querySelector<HTMLButtonElement>("[data-drawer-toggle]");
      if (toggle) toggle.setAttribute("aria-expanded", "false");
      track("virtual_pageview", { page_path: new URL(url, location.href).pathname, page_title: doc.title });
      arrive();
    } catch (e) {
      console.warn("navigation", e);
      location.href = href;
    } finally {
      busy = false;
    }
  };
  going = go;
  initDrawer();
  document.addEventListener("click", (e) => {
    const target = (e.target instanceof Element ? e.target : null) as Element | null;
    const a = target ? (target.closest("a[href]") as HTMLAnchorElement | null) : null;
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.target === "_blank" || a.hasAttribute("download") || a.origin !== location.origin) return;
    const raw = a.getAttribute("href") || "";
    if (raw.startsWith("#") || raw.startsWith("mailto:")) return;
    const url = new URL(a.href);
    if (skip(url.pathname)) return;
    e.preventDefault();
    go(url.href, true);
  });
  window.addEventListener("popstate", () => go(location.href, false));
  history.replaceState({ basho: 1 }, "", location.href);
}

function initDrawer() {
  const toggle = document.querySelector<HTMLButtonElement>("[data-drawer-toggle]");
  const scrim = document.querySelector<HTMLElement>("[data-drawer-scrim]");
  if (!toggle) return;

  const syncToggles = () => {
    const fxBtn = document.querySelector<HTMLButtonElement>(".toggle-pill[data-toggle='fx']");
    const bandBtn = document.querySelector<HTMLButtonElement>(".toggle-pill[data-toggle='band']");
    const sumoBtn = document.querySelector<HTMLButtonElement>(".toggle-pill[data-toggle='sumo']");
    const fxInput = document.querySelector<HTMLInputElement>("input[data-fx]");
    const bandInput = document.querySelector<HTMLInputElement>("input[data-band]");
    const sumoInput = document.querySelector<HTMLInputElement>("input[data-stage]");
    if (fxBtn && fxInput) {
      fxBtn.classList.toggle("active", fxInput.checked);
      fxBtn.setAttribute("aria-pressed", String(fxInput.checked));
    }
    if (bandBtn && bandInput) {
      bandBtn.classList.toggle("active", bandInput.checked);
      bandBtn.setAttribute("aria-pressed", String(bandInput.checked));
    }
    if (sumoBtn && sumoInput) {
      sumoBtn.classList.toggle("active", sumoInput.checked);
      sumoBtn.setAttribute("aria-pressed", String(sumoInput.checked));
    }
  };

  const setOpen = (open: boolean) => {
    document.documentElement.toggleAttribute("data-drawer-open", open);
    toggle.setAttribute("aria-expanded", String(open));
    if (open) syncToggles();
  };

  toggle.addEventListener("click", () => {
    const open = document.documentElement.hasAttribute("data-drawer-open");
    setOpen(!open);
  });

  if (scrim) {
    scrim.addEventListener("click", () => setOpen(false));
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && document.documentElement.hasAttribute("data-drawer-open")) {
      setOpen(false);
    }
  });

  document.querySelectorAll<HTMLButtonElement>(".toggle-pill[data-toggle]").forEach((pill) => {
    pill.addEventListener("click", (e) => {
      e.stopPropagation();
      const kind = pill.dataset.toggle;
      const targetInput =
        kind === "fx"
          ? document.querySelector<HTMLInputElement>("input[data-fx]")
          : kind === "band"
            ? document.querySelector<HTMLInputElement>("input[data-band]")
            : kind === "sumo"
              ? document.querySelector<HTMLInputElement>("input[data-stage]")
              : null;
      if (targetInput) {
        targetInput.click();
        syncToggles();
      }
    });
  });
  document.addEventListener("basho:sound", syncToggles);

  const nav = document.querySelector("header nav");
  if (nav) {
    nav.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(".toggle-pill")) return;
      if (target?.closest("a, button")) {
        setOpen(false);
      }
    });
  }
}
